/* eslint-disable max-lines */
import {
  CameraGroupIcon,
  MenuDropdown,
  RecordingDotIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import {
  ChevronRightIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  CAMERAS_TAB_ID,
  MultiCamGroupTab,
  activeTabAtom,
  activeTabIdAtom,
  committedCameraMapAtom,
  committedGroupMapAtom,
  removedFromGroupAtom,
  tabsAtom,
} from '../../state';
import useMultiCamSelection from '../../useMultiCamSelection';
import useTabRowFit from './useTabRowFit';
import { MAX_SELECTED_CAMERAS } from '../../util';

const pillClassName =
  'flex-shrink-0 cursor-pointer rounded-md border px-3 py-1 text-sm';

const activeClassName =
  'border-transparent bg-gray-400 dark:bg-gray-700 text-onlight-text-1 dark:text-dark-text';

const inactiveClassName =
  'border-onlight-line-3 dark:border-ondark-line-2 text-onlight-text-2 dark:text-ondark-text-2';

const sectionBoxClassName = 'flex items-center gap-2 rounded-lg border-2 px-3';

const activeSectionBoxClassName = 'border-primary-500';

const inactiveSectionBoxClassName = 'border-transparent';

const activeSectionTextClassName = 'text-onlight-text-1 dark:text-dark-text';

const inactiveSectionTextClassName =
  'text-onlight-text-2 dark:text-ondark-text-2';

const sectionIconClassName = (active: boolean) =>
  clsx(
    'flex h-8 w-8 flex-none items-center justify-center rounded-md',
    active
      ? 'bg-primary-600 text-white'
      : 'bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-2 dark:text-ondark-text-2'
  );

const closeButtonClassName =
  'text-onlight-text-3 dark:text-ondark-text-2 hover:text-onlight-text-1 dark:hover:text-dark-text';

const renderCount = (count: number) => (
  <span className="text-onlight-text-1 dark:text-dark-text flex items-center gap-1 rounded-full bg-gray-300 px-1.5 py-0.5 text-xs dark:bg-gray-600">
    <RecordingDotIcon className="h-3.5 w-3.5 flex-none" />
    {count}
  </span>
);

const renderMorePill = (count: number) => (
  <span
    className={clsx(
      pillClassName,
      'text-onlight-text-2 dark:text-ondark-text-2 flex items-center border-transparent'
    )}
  >
    +{count} More
    <ChevronRightIcon className="ml-1 h-4 w-4" />
  </span>
);

export function MultiCamTabs() {
  const tabs = useAtomValue(tabsAtom);
  const activeTab = useAtomValue(activeTabAtom);
  const setActiveTabId = useSetAtom(activeTabIdAtom);
  const activeTabId = activeTab?.id ?? null;
  const committedGroupMap = useAtomValue(committedGroupMapAtom);
  const committedCameraMap = useAtomValue(committedCameraMapAtom);
  const removedFromGroup = useAtomValue(removedFromGroupAtom);
  const { removeCameras, removeGroup } = useMultiCamSelection();

  const hasCameraTab = tabs.some((tab) => tab.type === 'cameras');
  const groupTabs = tabs.filter(
    (tab): tab is MultiCamGroupTab => tab.type === 'group'
  );
  const isGroupActive = groupTabs.some((tab) => tab.id === activeTabId);

  const { rowRef, measureRef, visibleTabs, overflowTabs } = useTabRowFit(
    groupTabs,
    activeTabId
  );

  const cameras = Object.values(committedCameraMap);
  const locationCount = new Set(
    cameras.map((camera) => camera.location?.id).filter(Boolean)
  ).size;

  const getCount = (tabId: string) => {
    const removed = (removedFromGroup[tabId] || []).length;
    const base = committedGroupMap[tabId]?.cameraCount || 0;
    return Math.min(Math.max(base - removed, 0), MAX_SELECTED_CAMERAS);
  };

  const onCloseCameras = () => {
    removeCameras(Object.keys(committedCameraMap));
    if (activeTabId === CAMERAS_TAB_ID) {
      setActiveTabId(null);
    }
  };

  const onClose = (tabId: string) => {
    removeGroup(tabId);
    if (activeTabId !== tabId) return;
    const index = groupTabs.findIndex((tab) => tab.id === tabId);
    const nextTab = groupTabs[index + 1] ?? groupTabs[index - 1];
    setActiveTabId(nextTab?.id ?? null);
  };

  const renderTab = (tab: MultiCamGroupTab) => (
    <div
      key={tab.id}
      onClick={() => setActiveTabId(tab.id)}
      className={clsx(
        pillClassName,
        'flex items-center gap-2',
        tab.id === activeTabId ? activeClassName : inactiveClassName
      )}
    >
      <span className="truncate" title={tab.name}>
        {tab.name}
      </span>
      {renderCount(getCount(tab.id))}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose(tab.id);
        }}
        className={closeButtonClassName}
        title="Close tab"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-1 items-stretch">
      {hasCameraTab && (
        <div
          onClick={() => setActiveTabId(CAMERAS_TAB_ID)}
          className={clsx(
            sectionBoxClassName,
            'flex-none cursor-pointer',
            activeTabId === CAMERAS_TAB_ID
              ? activeSectionBoxClassName
              : inactiveSectionBoxClassName
          )}
        >
          <span
            className={sectionIconClassName(activeTabId === CAMERAS_TAB_ID)}
          >
            <VideoCameraIcon className="h-5 w-5" />
          </span>
          <span
            className={clsx(
              'text-sm',
              activeTabId === CAMERAS_TAB_ID
                ? activeSectionTextClassName
                : inactiveSectionTextClassName
            )}
          >
            {locationCount} {locationCount === 1 ? 'Location' : 'Locations'}
          </span>
          {renderCount(cameras.length)}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCloseCameras();
            }}
            className={closeButtonClassName}
            title="Remove all cameras"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {groupTabs.length > 0 && (
        <div
          className={clsx(
            sectionBoxClassName,
            'min-w-0 flex-1',
            isGroupActive
              ? activeSectionBoxClassName
              : inactiveSectionBoxClassName
          )}
        >
          <span className={sectionIconClassName(isGroupActive)}>
            <CameraGroupIcon className="h-5 w-5" />
          </span>
          <div className="relative min-w-0 flex-1">
            <div ref={rowRef} className="flex flex-nowrap items-center gap-2">
              {visibleTabs.map(renderTab)}
              {overflowTabs.length > 0 && (
                <MenuDropdown
                  useOnlyMenuButtonNode
                  position="bottom-right"
                  items={overflowTabs.map((tab) => ({
                    name: `${tab.name} (${getCount(tab.id)} Cam)`,
                    onClick: () => setActiveTabId(tab.id),
                  }))}
                  menuButton={renderMorePill(overflowTabs.length)}
                />
              )}
            </div>
            <div
              aria-hidden
              className="pointer-events-none invisible absolute inset-0 overflow-hidden"
            >
              <div
                ref={measureRef}
                className="absolute left-0 top-0 flex flex-nowrap items-center gap-2"
              >
                {groupTabs.map(renderTab)}
                {renderMorePill(groupTabs.length)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiCamTabs;
